import React, { useState } from 'react';
import dayjs from 'dayjs';
import { VehicleChangeEntry } from '@/services/api/vehicles';
import { VehicleHandoverHistory } from '@/services/api/handovers';
import { useNavigate } from 'react-router-dom';
import { getBusinessUnitLabel, Repossession } from '@/types/common';
import { getCompanyEntityLabel } from '@/types/purchase-order';

interface VehicleHistoryLogProps {
  changes: VehicleChangeEntry[];
  handovers: VehicleHandoverHistory[];
  repossessions: Repossession[];
}

const formatDateTime = (value: string) => {
  if (!value) return { date: '-', time: '' };

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return { date: value, time: '' };

  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(parsed);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);

  return { date, time };
};

const formatFieldLabel = (field: string) => {
  return field
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
};

const formatValue = (value: any, field?: string): string => {
  if (value === null || value === undefined) return '-';
  if (field === 'businessUnit') return getBusinessUnitLabel(value);
  if (field === 'entity') return getCompanyEntityLabel(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/** Extract the changed fields from before/after objects, skipping ID fields */
const getChangedFields = (entry: VehicleChangeEntry): string[] => {
  const allKeys = new Set([
    ...Object.keys(entry.before || {}),
    ...Object.keys(entry.after || {}),
  ]);
  // Filter out ID-like keys to show only human-readable fields
  return Array.from(allKeys).filter((k) => !k.toLowerCase().endsWith('id'));
};

/** "MMM DD, YYYY" for the timeline header, or a dash when missing/invalid. */
const formatRangeDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MMM DD, YYYY') : '-';
};

/** "DD/MM/YYYY HH:mm:ss" for the card fields, or a dash when missing/invalid. */
const formatFieldDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm:ss') : '-';
};

const formatDuration = (days: number): string => {
  const value = days || 0;
  return `${value} ${value <= 1 ? 'Day' : 'Days'}`;
};

const getInitials = (name?: string | null): string => {
  if (!name) return '-';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '-';
  const initials = (parts[0][0] || '') + (parts[1]?.[0] || '');
  return initials.toUpperCase();
};

const HandoverField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <div className="text-xs text-gray-400 mb-0.5">{label}</div>
    <div className="text-sm text-gray-900 font-semibold break-words">
      {value}
    </div>
  </div>
);

type HistoryTab = 'changes' | 'handover' | 'repossession';

const TabButton: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    onClick={onClick}
    className={`whitespace-nowrap py-3 border-b-2 text-[16px] font-medium ${
      active
        ? 'border-[#5E2AAC] text-gray-800'
        : 'border-transparent text-gray-400 hover:text-gray-500'
    }`}
  >
    {label}
    <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-semibold text-gray-600">
      {count}
    </span>
  </button>
);

const VehicleHistoryLog: React.FC<VehicleHistoryLogProps> = ({
  changes,
  handovers,
  repossessions,
}) => {
  const [activeTab, setActiveTab] = useState<HistoryTab>('changes');
  const navigate = useNavigate();

  return (
    <div className="w-full space-y-6 lg:flex-1">
      <div className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 px-5">
          <nav className="flex space-x-6">
            <TabButton
              label="Changes Data History"
              count={changes.length}
              active={activeTab === 'changes'}
              onClick={() => setActiveTab('changes')}
            />
            <TabButton
              label="History Handover"
              count={handovers.length}
              active={activeTab === 'handover'}
              onClick={() => setActiveTab('handover')}
            />
            <TabButton
              label="History Repossession"
              count={repossessions.length}
              active={activeTab === 'repossession'}
              onClick={() => setActiveTab('repossession')}
            />
          </nav>
        </div>

        <div className="bg-gray-50 px-5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Detail
        </div>

        {activeTab === 'changes' &&
          (changes.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-gray-400">
              No history available
            </div>
          ) : (
            <div className="px-5 py-4 space-y-0">
              {changes.map((entry, index) => {
                const { date, time } = formatDateTime(entry.createdAt);
                const isLast = index === changes.length - 1;
                const fields = getChangedFields(entry);
                const isFirst = index === 0;

                return (
                  <div key={entry.ID || index} className="flex gap-3">
                    {/* Timeline icon + line */}
                    <div className="flex flex-col items-center">
                      <i
                        className={`${
                          isFirst
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-checkbox-circle-line'
                        } text-xl text-[#5e2aac] flex-shrink-0`}
                      />
                      {!isLast && (
                        <div className="w-px flex-1 bg-gray-200 min-h-[40px]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-900">
                          {date}
                          {time && (
                            <span className="ml-2 text-gray-400">
                              | {time}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <i className="ri-user-line text-sm" />
                          {entry.createdBy || '-'}
                        </div>
                      </div>

                      {/* Reason — set by flows that require a justification
                          (an entity / business unit correction, for one) */}
                      {entry.reason && (
                        <div className="mb-3 flex gap-1.5 text-xs text-gray-600">
                          <i className="ri-chat-quote-line text-sm text-gray-400 flex-shrink-0" />
                          <span className="break-words">{entry.reason}</span>
                        </div>
                      )}

                      {/* Before / After table */}
                      <div className="rounded border border-gray-200 overflow-hidden">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="px-4 py-2 text-left font-normal text-gray-400 w-1/2">
                                Before
                              </th>
                              <th className="px-4 py-2 text-left font-normal text-gray-400 w-1/2 border-l border-gray-200">
                                After
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {fields.map((field) => (
                              <tr
                                key={field}
                                className="border-t border-gray-100"
                              >
                                <td className="px-4 py-2.5 align-top w-1/2">
                                  <div className="text-xs text-gray-400 mb-0.5">
                                    {formatFieldLabel(field)}
                                  </div>
                                  <div className="text-sm text-gray-900 font-semibold">
                                    {formatValue(entry.before?.[field], field)}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 align-top w-1/2 border-l border-gray-200">
                                  <div className="text-xs text-gray-400 mb-0.5">
                                    {formatFieldLabel(field)}
                                  </div>
                                  <div className="text-sm text-gray-900 font-semibold">
                                    {formatValue(entry.after?.[field], field)}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        {activeTab === 'handover' &&
          (handovers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 mb-4">
                <i className="ri-file-search-line text-4xl text-gray-300" />
              </div>
              <p className="text-base font-semibold text-gray-800">
                No History Handover to Display Yet
              </p>
              <p className="mt-1 text-sm text-gray-400">
                History Handover will be displayed here.
              </p>
            </div>
          ) : (
            <div className="px-5 py-4 space-y-0">
              {handovers.map((item, index) => {
                const isLast = index === handovers.length - 1;
                const isFirst = index === 0;
                const actor = item.updatedBy || item.createdBy || '-';

                return (
                  <div key={item.ID || index} className="flex gap-3">
                    {/* Timeline icon + line */}
                    <div className="flex flex-col items-center">
                      <i
                        className={`${
                          isFirst
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-checkbox-circle-line'
                        } text-xl text-[#5e2aac] flex-shrink-0`}
                      />
                      {!isLast && (
                        <div className="w-px flex-1 bg-gray-200 min-h-[40px]" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-900">
                          {formatRangeDate(item.handoverDate)}
                          <span className="mx-2 text-gray-400">&rarr;</span>
                          {formatRangeDate(item.returnDate)}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <i className="ri-user-line text-sm" />
                          {actor}
                        </div>
                      </div>

                      {/* Handover card */}
                      <div className="rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#F3EDFB] text-sm font-semibold text-[#5E2AAC]">
                              {getInitials(item.driverName)}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {item.driverName || '-'}
                              </div>
                              <div className="text-xs text-gray-400 truncate">
                                {item.driverCode || '-'}
                              </div>
                            </div>
                          </div>
                          <span className="flex-shrink-0 rounded bg-[#F3EDFB] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#5E2AAC]">
                            {item.status || 'RETURNED'}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                          <HandoverField
                            label="Business Unit"
                            value={
                              item.vehicleBusinessUnit
                                ? getBusinessUnitLabel(item.vehicleBusinessUnit)
                                : '-'
                            }
                          />
                          <HandoverField
                            label="Hub Location"
                            value={item.vehicleHubName || '-'}
                          />
                          <HandoverField
                            label="Client"
                            value={item.clientName || '-'}
                          />
                          <HandoverField
                            label="Pitstop"
                            value={item.hubName || '-'}
                          />
                          <HandoverField
                            label="Handover Date"
                            value={formatFieldDate(item.handoverDate)}
                          />
                          <HandoverField
                            label="Return Date"
                            value={formatFieldDate(item.returnDate)}
                          />
                          <HandoverField
                            label="Duration"
                            value={formatDuration(item.returnDuration)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        {activeTab === 'repossession' &&
          (repossessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-5 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gray-100 mb-4">
                <i className="ri-file-search-line text-4xl text-gray-300" />
              </div>
              <p className="text-base font-semibold text-gray-800">
                No History Repossession to Display Yet
              </p>
              <p className="mt-1 text-sm text-gray-400">
                History Repossession will be displayed here.
              </p>
            </div>
          ) : (
            <div className="px-5 py-4 space-y-0">
              {repossessions.map((item, index) => {
                const isLast = index === repossessions.length - 1;
                const isFirst = index === 0;

                return (
                  <div key={item.ID || index} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <i
                        className={`${
                          isFirst
                            ? 'ri-checkbox-circle-fill'
                            : 'ri-checkbox-circle-line'
                        } text-xl text-[#5e2aac] flex-shrink-0`}
                      />
                      {!isLast && (
                        <div className="w-px flex-1 bg-gray-200 min-h-[40px]" />
                      )}
                    </div>

                    <div className="flex-1 pb-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-gray-900">
                          {formatRangeDate(item.createdDate)}
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <i className="ri-user-line text-sm" />
                          {item.assignedTo || item.assignedOpsTeam || '-'}
                        </div>
                      </div>

                      {/* Card opens the case — the timeline is a pointer, the
                          repossession page owns the investigation trail. */}
                      <div
                        role="button"
                        tabIndex={0}
                        className="rounded-lg border border-gray-200 p-4 cursor-pointer hover:border-[#5E2AAC]"
                        onClick={() => navigate(`/repossessions/${item.ID}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            navigate(`/repossessions/${item.ID}`);
                          }
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-gray-900">
                            {item.code}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="rounded bg-gray-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                              {item.severity}
                            </span>
                            <span className="rounded bg-[#F3EDFB] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#5E2AAC]">
                              {item.status}
                            </span>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                          <HandoverField
                            label="Mitra"
                            value={item.driverName || '-'}
                          />
                          <HandoverField
                            label="Reason"
                            value={item.reason || '-'}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
      </div>
    </div>
  );
};

export default VehicleHistoryLog;
